import { csrfHeaders } from './csrf';
import { pushEvent } from './gtm';
import { getLocalStorageItem } from './storage';

type GcmConsent = {
    analyticsStorage?: string;
    adStorage?: string;
    adPersonalization?: string;
    adUserData?: string;
};

type UcService = {
    name?: string;
    consent?: boolean;
};

type UcData = {
    gcm?: GcmConsent;
    consent?: {
        gcm?: GcmConsent;
        services?: Record<string, UcService>;
    };
    ui?: {
        language?: string;
    };
};

declare global {
    interface Window {
        ucData?: UcData;
        updateConsentFromUcData?: (reason?: string) => void;
    }
}

function resolveServices(ucData: UcData): UcService[] {
    return Object.values(ucData.consent?.services ?? {});
}

function mapConsent(ucData?: UcData, reason?: string): { analytics: boolean; marketing: boolean } | null {
    if (!ucData) return null;

    const gcm = ucData.gcm || ucData.consent?.gcm;
    const services = resolveServices(ucData);
    const analyticsServiceConsent = services.some(
        (service) => service.consent === true && (service.name || '').toLowerCase().includes('analytics'),
    );
    const marketingServiceConsent = services.some((service) => {
        if (service.consent !== true) {
            return false;
        }

        const normalizedName = (service.name || '').toLowerCase();

        return [
            'ads',
            'ad ',
            'advert',
            'remarketing',
            'marketing',
            'facebook',
            'meta',
            'tiktok',
            'linkedin',
            'google ads',
        ].some((keyword) => normalizedName.includes(keyword));
    });

    if (!gcm && services.length === 0) return null;

    const analytics = gcm?.analyticsStorage === 'granted' || analyticsServiceConsent;
    let marketing =
        gcm?.adStorage === 'granted' ||
        gcm?.adPersonalization === 'granted' ||
        gcm?.adUserData === 'granted' ||
        marketingServiceConsent;

    const allServicesAccepted = services.length > 0 && services.every((service) => service.consent === true);

    if (reason === 'UC_UI_ACCEPT_ALL' && allServicesAccepted) {
        marketing = true;
    }

    return { analytics, marketing };
}

function parseGaClientIdFromCookie(): string | null {
    const gaCookie = document.cookie
        .split('; ')
        .find((cookie) => cookie.startsWith('_ga='));

    if (!gaCookie) {
        return null;
    }

    const value = gaCookie.split('=')[1] || '';
    const parts = value.split('.');

    if (parts.length < 4) {
        return null;
    }

    return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
}

function parseGaSessionIdFromCookie(): string | null {
    const gaSessionCookie = document.cookie
        .split('; ')
        .find((cookie) => cookie.startsWith('_ga_'));

    if (!gaSessionCookie) {
        return null;
    }

    const rawValue = gaSessionCookie.split('=')[1] || '';
    let value = rawValue;

    try {
        value = decodeURIComponent(rawValue);
    } catch {
        value = rawValue;
    }

    const gs2Match = value.match(/(?:^|[.$])s(\d+)/);

    if (gs2Match?.[1]) {
        return gs2Match[1];
    }

    const parts = value.split('.');

    if (parts.length < 3) {
        return null;
    }

    const sessionPart = parts[2] || '';

    if (/^\d+$/.test(sessionPart)) {
        return sessionPart;
    }

    if (sessionPart.startsWith('s') && /^\d+$/.test(sessionPart.slice(1))) {
        return sessionPart.slice(1);
    }

    return null;
}

function parseFbpFromCookie(): string | null {
    const fbpCookie = document.cookie
        .split('; ')
        .find((cookie) => cookie.startsWith('_fbp='));

    if (!fbpCookie) {
        return null;
    }

    const rawValue = fbpCookie.split('=')[1] || '';

    try {
        return decodeURIComponent(rawValue) || null;
    } catch {
        return rawValue || null;
    }
}

function parseFbcFromCookie(): string | null {
    const fbcCookie = document.cookie
        .split('; ')
        .find((cookie) => cookie.startsWith('_fbc='));

    if (!fbcCookie) {
        return null;
    }

    const rawValue = fbcCookie.split('=')[1] || '';

    try {
        return decodeURIComponent(rawValue) || null;
    } catch {
        return rawValue || null;
    }
}

function parseUcDataFromLocalStorage(): UcData | null {
    const candidates = ['ucData', 'UC_DATA', 'usercentrics', 'uc_user_interaction'];

    for (const key of candidates) {
        const raw = getLocalStorageItem(key);

        if (!raw) {
            continue;
        }

        try {
            const parsed = JSON.parse(raw) as UcData;
            if (parsed?.gcm || parsed?.consent?.gcm) {
                return parsed;
            }
        } catch {
            continue;
        }
    }

    return null;
}

function resolveUcData(): UcData | undefined {
    return window.ucData ?? parseUcDataFromLocalStorage() ?? undefined;
}

async function postConsent(consent: { analytics: boolean; marketing: boolean }, rawPayload?: UcData): Promise<void> {
    try {
        const response = await fetch('/consent', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...csrfHeaders(),
            },
            body: JSON.stringify({
                analytics: consent.analytics,
                marketing: consent.marketing,
                source: 'usercentrics',
                raw_payload: rawPayload || null,
                ga_client_id: parseGaClientIdFromCookie(),
                ga_session_id: parseGaSessionIdFromCookie(),
                fbp: parseFbpFromCookie(),
                fbc: parseFbcFromCookie(),
            }),
            credentials: 'same-origin',
        });

        if (!response.ok) {
            const errorBody = await response.text().catch(() => 'No response body');
            throw new Error(`Consent update failed with status ${response.status}: ${errorBody}`);
        }
    } catch (error) {
        // Enhanced error logging with structured context
        console.error('[Consent] Failed to persist consent to backend:', {
            error: error instanceof Error ? error.message : String(error),
            consent,
            timestamp: new Date().toISOString(),
            ga_client_id: parseGaClientIdFromCookie(),
            ga_session_id: parseGaSessionIdFromCookie(),
        });

        // Re-throw to allow caller to handle
        throw error;
    }
}

export function initConsentTracking(): void {
    let lastConsentSignature = '';
    const isDebugMode = window.location.search.includes('tracking_debug=1');

    window.updateConsentFromUcData = (reason = 'manual') => {
        const ucData = resolveUcData();
        const consent = mapConsent(ucData, reason);

        if (!consent) {
            return;
        }

        if (isDebugMode) {
            const consentDebug = {
                reason,
                mapped: consent,
                gcm: ucData?.gcm || ucData?.consent?.gcm || null,
                services: resolveServices(ucData || {}),
            };
            console.debug('Consent debug', consentDebug);
        }

        const signature = `${consent.analytics}:${consent.marketing}`;

        if (signature === lastConsentSignature) {
            return;
        }

        lastConsentSignature = signature;

        pushEvent('consent_update', {
            consent_analytics: consent.analytics,
            consent_marketing: consent.marketing,
        });

        void postConsent(consent, ucData).catch((error) => {
            // Structured error logging for production debugging
            console.error('[Consent] Persistence failed:', {
                error: error instanceof Error ? error.message : String(error),
                consent,
                timestamp: new Date().toISOString(),
                ucData: {
                    gcm: ucData?.gcm || ucData?.consent?.gcm || null,
                    services_count: Object.keys(ucData?.consent?.services || {}).length,
                },
            });
        });
    };

    if (resolveUcData()) {
        window.updateConsentFromUcData('initial');
    }

    const consentEvents = [
        'UC_CONSENT',
        'UC_UI_INITIALIZED',
        'UC_UI_CONSENT_CHANGED',
        'UC_UI_ACCEPT_ALL',
        'UC_UI_DENY_ALL',
        'UC_UI_SETTINGS_SAVE',
    ] as const;

    for (const eventName of consentEvents) {
        window.addEventListener(eventName, () => {
            window.updateConsentFromUcData?.(eventName);

            setTimeout(() => {
                window.updateConsentFromUcData?.(`${eventName}_delayed_1000ms`);
            }, 1000);
        });
    }

    setTimeout(() => {
        window.updateConsentFromUcData?.('timeout');
    }, 1500);
}
